import React, { useEffect, useMemo, useState } from 'react';

export default function KpaCascadeModal({
                                            show,
                                            onClose,
                                            loadManagerKpas,    // () => Promise<{manager_contract_id, items:[...]}>
                                            remaining,          // number (remaining capacity out of 90)
                                            onSubmitCascade,    // (payload) => Promise
                                        }) {
    const [loading, setLoading] = useState(false);
    const [managerItems, setManagerItems] = useState([]);
    const [selected, setSelected] = useState({});
    const [overrides, setOverrides] = useState({});
    const [submitBusy, setSubmitBusy] = useState(false);
    const [error, setError] = useState(null);

    // load manager KPAs on open
    useEffect(() => {
        if (!show) return;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await loadManagerKpas();
                const items = data?.items ?? [];
                setManagerItems(items);
                // preselect all (you can change to none if you prefer)
                const sel = {}; items.forEach(i => sel[i.id] = true);
                setSelected(sel);
                // default overrides: weight = manager weight (editable)
                const ov = {}; items.forEach(i => ov[i.id] = {
                    id: i.id, weight: i.weight, name: i.name, timeframe_id: i.timeframe_id, alignment: i.alignment, description: i.description
                });
                setOverrides(ov);
            } catch (e) {
                setError(e?.response?.data?.message || 'Failed to load manager KPAs.');
            } finally {
                setLoading(false);
            }
        })();
    }, [show]);

    const chosen = useMemo(() => managerItems.filter(i => !!selected[i.id]), [managerItems, selected]);
    const totalRequested = useMemo(() =>
        chosen.reduce((sum, i) => sum + Number(overrides[i.id]?.weight || i.weight || 0), 0), [chosen, overrides]
    );

    const handleToggle = (id, on) => setSelected(s => ({ ...s, [id]: on }));
    const handleAll = (on) => {
        const next = {}; managerItems.forEach(i => next[i.id] = on);
        setSelected(next);
    };
    const setOv = (id, patch) => setOverrides(o => ({ ...o, [id]: { ...(o[id] || { id }), ...patch }}));

    const canSubmit = chosen.length > 0 && remaining > 0;

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitBusy(true);
        setError(null);
        try {
            const ids = chosen.map(i => i.id);
            const ovrList = ids.map(id => overrides[id]).filter(Boolean);
            await onSubmitCascade({
                mode: 'selected',
                kpa_ids: ids,
                overrides: ovrList,
            });
            onClose();
        } catch (e) {
            setError(e?.response?.data?.message || 'Cascade failed.');
        } finally {
            setSubmitBusy(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Cascade KPAs from Manager</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger">{error}</div>}
                        {loading ? (
                            <div className="text-muted">Loading…</div>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => handleAll(true)}>Select All</button>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleAll(false)}>Clear</button>
                                    </div>
                                    <div>
                                        <span className="badge bg-secondary me-2">Remaining Capacity: {remaining.toFixed(2)} / 90</span>
                                        <span className={`badge ${totalRequested > remaining ? 'bg-danger' : 'bg-info'}`}>
                      Requested Total: {totalRequested.toFixed(2)}
                    </span>
                                    </div>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0 align-middle">
                                        <thead className="table-light">
                                        <tr>
                                            <th style={{width: 40}}></th>
                                            <th>Name</th>
                                            <th style={{width: 120}} className="text-end">Mgr Weight</th>
                                            <th style={{width: 140}} className="text-end">Cascade Weight</th>
                                            <th style={{width: 160}}>Timeframe</th>
                                            <th style={{width: 260}}>Alignment</th>
                                            <th style={{width: 260}}>Description</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {managerItems.length === 0 && (
                                            <tr><td colSpan="7" className="text-center text-muted py-4">No manager KPAs.</td></tr>
                                        )}
                                        {managerItems.map(i => {
                                            const ov = overrides[i.id] || {};
                                            const selectedRow = !!selected[i.id];
                                            return (
                                                <tr key={i.id} className={!selectedRow ? 'table-secondary' : ''}>
                                                    <td>
                                                        <input type="checkbox" className="form-check-input" checked={selectedRow} onChange={e => handleToggle(i.id, e.target.checked)} />
                                                    </td>
                                                    <td>
                                                        <input className="form-control form-control-sm" value={ov.name ?? i.name} onChange={e => setOv(i.id, { name: e.target.value })} />
                                                    </td>
                                                    <td className="text-end">{Number(i.weight).toFixed(2)}</td>
                                                    <td className="text-end" style={{maxWidth:140}}>
                                                        <input type="number" step="0.01" min="0"
                                                               className={`form-control form-control-sm ${Number(ov.weight ?? i.weight) < 0 ? 'is-invalid' : ''}`}
                                                               value={ov.weight ?? i.weight}
                                                               onChange={e => setOv(i.id, { weight: Number(e.target.value) })}
                                                        />
                                                    </td>
                                                    <td>
                                                        {/* simple select by id; replace with real TF list if you wish */}
                                                        <input className="form-control form-control-sm"
                                                               value={ov.timeframe_id ?? i.timeframe_id ?? ''}
                                                               onChange={e => setOv(i.id, { timeframe_id: e.target.value || null })}
                                                               placeholder={i?.timeframe?.name || ''} />
                                                    </td>
                                                    <td>
                                                        <input className="form-control form-control-sm"
                                                               value={ov.alignment ?? i.alignment ?? ''}
                                                               onChange={e => setOv(i.id, { alignment: e.target.value })}/>
                                                    </td>
                                                    <td>
                                                        <input className="form-control form-control-sm"
                                                               value={ov.description ?? i.description ?? ''}
                                                               onChange={e => setOv(i.id, { description: e.target.value })}/>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-outline-secondary" onClick={onClose} disabled={submitBusy}>Cancel</button>
                        <button className="btn btn-primary" onClick={submit} disabled={!canSubmit || submitBusy}>
                            {submitBusy ? 'Cascading…' : 'Cascade Selected'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
